import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { CheckCircle2, Circle, Users, TrendingUp, Trophy, CalendarDays, ArrowRight, ShieldAlert } from "lucide-react";
import { api } from "@/api";
import { LeadBadge, LEAD_ORDER, LEAD_STYLE, fmtDate } from "@/components/bp/bpLabels";
import { EarningsBoard } from "@/components/bp/EarningsBoard";
import { vigSentence } from "@/components/bp/vigilanceLabels";

const Step = ({ done, label, to, testid }) => (
  <Link to={to} className={`flex items-center gap-3 p-3 border transition-colors ${done ? "border-[#A7F3D0] bg-[#ECFDF5]" : "border-[#DEE2E6] bg-white hover:border-[#0A0A0A]"}`} data-testid={testid} data-done={done ? "1" : "0"}>
    {done ? <CheckCircle2 className="h-5 w-5 text-[#059669]" /> : <Circle className="h-5 w-5 text-[#868E96]" />}
    <span className={`text-sm font-semibold flex-1 ${done ? "text-[#065F46]" : ""}`}>{label}</span>
    {!done && <ArrowRight className="h-4 w-4 text-[#868E96]" />}
  </Link>
);

export default function BpDashboard() {
  const { me } = useOutletContext();
  const [earnings, setEarnings] = useState(null);
  useEffect(() => { if (me && !me.error && !me.must_change_password) api.get("/bp/earnings").then((r) => setEarnings(r.data)).catch(() => setEarnings(null)); }, [me]);
  if (!me || me.error) return <div className="p-10 text-sm text-[#868E96]">Chargement…</div>;
  const ob = me.onboarding || {};
  return (
    <div className="p-6 lg:p-10 space-y-8" data-testid="bp-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Espace Business Partner</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Bonjour {me.prenom}.</h1>
        <p className="text-sm text-[#495057] mt-2">{me.zone ? `Zone : ${me.zone} · ` : ""}{me.company_name || "Structure à renseigner"}{me.siret_formatted ? ` · SIRET ${me.siret_formatted}` : ""}</p>
      </div>

      {!me.onboarding_done && (
        <section className="bg-[#0A0A0A] text-white p-6" data-testid="bp-onboarding">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] font-bold mb-3">Onboarding</div>
          <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-2 text-[#0A0A0A]">
            <Step done={ob.password_changed} label="Mot de passe personnel choisi" to="/bp/profil" testid="bp-step-password" />
            <Step done={ob.rules_accepted} label="Règlement Business Partner accepté" to="/bp/reglement" testid="bp-step-rules" />
            <Step done={ob.profile_complete} label="Profil complété (zone, structure, SIRET)" to="/bp/profil" testid="bp-step-profile" />
            <Step done={ob.contract_signed} label="Contrat Business Partner signé" to="/bp/contrat" testid="bp-step-contract" />
            <Step done={ob.first_lead} label="Premier lead enregistré" to="/bp/leads?new=1" testid="bp-step-lead" />
          </div>
        </section>
      )}

      {me.vigilance && !me.vigilance.ok && ob.profile_complete && (
        <Link to="/bp/profil#vigilance" className={`flex items-start gap-3 p-4 border-2 text-xs ${me.vigilance.state === "pending" ? "border-[#FFD700] bg-[#FFFBEB]" : "border-[#E60000] bg-[#FEF2F2]"}`} data-testid="bp-vigilance-alert" data-state={me.vigilance.state}>
          <ShieldAlert className={`h-5 w-5 shrink-0 ${me.vigilance.state === "pending" ? "text-[#B45309]" : "text-[#E60000]"}`} />
          <div><div className="font-bold uppercase tracking-wider text-[10px]">Attestation de vigilance URSSAF — {me.vigilance.label}</div><div className="mt-0.5">{vigSentence(me.vigilance)}</div><div className="mt-1 font-bold text-[#002FA7] underline">{me.vigilance.state === "pending" ? "Voir mon dépôt" : "Déposer mon attestation →"}</div></div>
        </Link>
      )}

      <section data-testid="bp-dashboard-earnings">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Mes gains</div>
          <Link to="/bp/gains" className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7] hover:underline" data-testid="bp-dashboard-gains-link">Détail des commissions →</Link>
        </div>
        <EarningsBoard data={earnings} compact />
      </section>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          [Users, "Leads au total", me.leads_total, "#002FA7", "kpi-total"],
          [CalendarDays, "Ce mois-ci", me.stats?.leads_month, "#0EA5E9", "kpi-month"],
          [TrendingUp, "En cours", (me.leads_by_status?.contacted || 0) + (me.leads_by_status?.qualified || 0) + (me.leads_by_status?.demo || 0), "#7C3AED", "kpi-progress"],
          [Trophy, "Gagnés", `${me.leads_by_status?.won || 0} · ${me.stats?.won_rate || 0} %`, "#059669", "kpi-won"],
        ].map(([Icon, label, val, color, tid]) => (
          <div key={label} className="bg-white border border-[#DEE2E6] p-4" data-testid={`bp-${tid}`}>
            <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>{label}</div><Icon className="h-4 w-4" style={{ color }} /></div>
            <div className="font-display font-black text-3xl mt-1">{val ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DEE2E6]">
          <div className="px-4 py-3 border-b border-[#DEE2E6] text-[10px] uppercase tracking-[0.25em] font-bold">Pipeline</div>
          <div className="p-4 grid grid-cols-3 gap-2">
            {LEAD_ORDER.map((k) => (
              <div key={k} className="bg-[#F8F9FA] p-3" data-testid={`bp-pipeline-${k}`}>
                <div className="font-display font-black text-2xl">{me.leads_by_status?.[k] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#495057]">{LEAD_STYLE[k].label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#DEE2E6]">
          <div className="px-4 py-3 border-b border-[#DEE2E6] flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Derniers leads</span>
            <Link to="/bp/leads?new=1" className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7] hover:underline" data-testid="bp-dashboard-new-lead">+ Nouveau lead</Link>
          </div>
          {(me.recent_leads || []).length === 0 ? <div className="p-6 text-xs text-[#868E96]" data-testid="bp-no-leads">Aucun lead pour l'instant.</div> : (
            <div className="divide-y divide-[#DEE2E6]">
              {me.recent_leads.map((l) => (
                <Link key={l.id} to={`/bp/leads?lead=${l.id}`} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#F8F9FA]" data-testid={`bp-recent-${l.id}`}>
                  <span className="text-[10px] font-mono text-[#868E96]">{l.reference}</span>
                  <span className="text-sm font-semibold flex-1 truncate">{l.company_name}</span>
                  <span className="text-[10px] text-[#868E96]">{fmtDate(l.created_at)}</span>
                  <LeadBadge status={l.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
